import React, { ComponentProps, memo } from "react";

import { nonNegativeRule, percentRangeRule, positiveRequiredRule, useTranslation } from "utils";

import { CROP_YEAR_FIELD_LIMITS, CropDetailsFieldRule, CropDetailsNumberFieldConfig, HelpFieldKey } from "consts";

import { InputNumber } from "components";

import { HelpFieldWrapper } from "pages/app/scenario-analysis/components/HelpFieldWrapper";

export type CropDetailsFieldRules = NonNullable<ComponentProps<typeof InputNumber>["rules"]>;

const RULE_BUILDERS: Record<CropDetailsFieldRule, (message: string) => CropDetailsFieldRules[number]> = {
  positiveRequired: positiveRequiredRule,
  percentRange: percentRangeRule,
  nonNegative: nonNegativeRule,
};

interface CropDetailsNumberFieldProps {
  field: CropDetailsNumberFieldConfig;
  label: string;
  suffix?: string;
  initialValue: number | null | undefined;
  onFocus: (fieldKey: HelpFieldKey) => void;
  /** Rules checked after the field's own rule; validation stops at the first failure. */
  extraRules?: CropDetailsFieldRules;
}

/** One numeric crop details input, wrapped so focusing it shows its guidance. */
export const CropDetailsNumberField = memo(({ field, label, suffix, initialValue, onFocus, extraRules }: CropDetailsNumberFieldProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { name, placeholder, rule, ruleMessageKey } = field;
  const ownRule = RULE_BUILDERS[rule](t(ruleMessageKey));

  return (
    <HelpFieldWrapper fieldKey={name} onFocus={onFocus}>
      <InputNumber
        name={name}
        label={label}
        suffix={suffix}
        initialValue={initialValue ?? undefined}
        {...CROP_YEAR_FIELD_LIMITS[name]}
        placeholder={placeholder}
        validateTrigger="onBlur"
        {...(extraRules ? { validateFirst: true, rules: [ownRule, ...extraRules] } : { rules: [ownRule] })}
      />
    </HelpFieldWrapper>
  );
});
